/**
 * Arial glyph widths as fractions of the font size (em units)
 * These values represent the relative width of each character
 */
const ARIAL_GLYPH_WIDTHS: Record<string, number> = {
  // Space and punctuation
  ' ': 0.278,
  '!': 0.278,
  '"': 0.355,
  '#': 0.556,
  $: 0.556,
  '%': 0.889,
  '&': 0.667,
  "'": 0.191,
  '(': 0.333,
  ')': 0.333,
  '*': 0.389,
  '+': 0.584,
  ',': 0.278,
  '-': 0.333,
  '.': 0.278,
  '/': 0.278,
  // Numbers
  '0': 0.556,
  '1': 0.556,
  '2': 0.556,
  '3': 0.556,
  '4': 0.556,
  '5': 0.556,
  '6': 0.556,
  '7': 0.556,
  '8': 0.556,
  '9': 0.556,
  ':': 0.278,
  ';': 0.278,
  '<': 0.584,
  '=': 0.584,
  '>': 0.584,
  '?': 0.556,
  '@': 1.015,
  // Uppercase letters
  A: 0.667,
  B: 0.667,
  C: 0.722,
  D: 0.722,
  E: 0.667,
  F: 0.611,
  G: 0.778,
  H: 0.722,
  I: 0.278,
  J: 0.5,
  K: 0.667,
  L: 0.556,
  M: 0.833,
  N: 0.722,
  O: 0.778,
  P: 0.667,
  Q: 0.778,
  R: 0.722,
  S: 0.667,
  T: 0.611,
  U: 0.722,
  V: 0.667,
  W: 0.944,
  X: 0.667,
  Y: 0.667,
  Z: 0.611,
  // Lowercase letters
  a: 0.556,
  b: 0.556,
  c: 0.5,
  d: 0.556,
  e: 0.556,
  f: 0.278,
  g: 0.556,
  h: 0.556,
  i: 0.222,
  j: 0.222,
  k: 0.5,
  l: 0.222,
  m: 0.833,
  n: 0.556,
  o: 0.556,
  p: 0.556,
  q: 0.556,
  r: 0.333,
  s: 0.5,
  t: 0.278,
  u: 0.556,
  v: 0.5,
  w: 0.722,
  x: 0.5,
  y: 0.5,
  z: 0.5,
  // Additional characters
  '[': 0.278,
  '\\': 0.278,
  ']': 0.278,
  '^': 0.469,
  _: 0.556,
  '`': 0.333,
  '{': 0.334,
  '|': 0.26,
  '}': 0.334,
  '~': 0.584,
}

/**
 * Default width for characters not in the glyph set
 * Using average width of common characters
 */
const DEFAULT_GLYPH_WIDTH = 0.556

/**
 * Calculates the width of a text string using Arial glyph metrics
 * @param text - The text string to measure
 * @param fontSize - The font size in pixels
 * @param unknownGlyphWidth - em width assumed for characters outside the glyph set
 * @returns The calculated width in pixels
 */
export const calculateTextWidth = (
  text: string,
  fontSize: number,
  unknownGlyphWidth = DEFAULT_GLYPH_WIDTH,
): number => {
  let totalWidth = 0

  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    const glyphWidth = ARIAL_GLYPH_WIDTHS[char] ?? unknownGlyphWidth
    totalWidth += glyphWidth * fontSize
  }

  return totalWidth
}

/** Matches Text3DThree's default per-line width scaling. */
export const TEXT_3D_ADDITIONAL_WIDTH_SCALE = 1.4

/** Matches Text3DThree's default background/border inset in world units. */
export const TEXT_3D_LABEL_MARGIN_WORLD = 0.01

/** Matches Text3DThree's default gap above and below each line, relative to the font size. */
export const TEXT_3D_LINE_GAP_FACTOR = 0.2

export const calculateText3DLineWidth = (
  text: string,
  fontSize: number,
  widthScale = TEXT_3D_ADDITIONAL_WIDTH_SCALE,
): number => calculateTextWidth(text, fontSize) * widthScale

/** Background plane width used by Text3DThree (and Comment3D labels). */
export const calculateText3DLabelRectangleWidth = (
  textLines: string[],
  fontSize: number,
  widthScale = TEXT_3D_ADDITIONAL_WIDTH_SCALE,
  marginWorld = TEXT_3D_LABEL_MARGIN_WORLD,
): number => {
  const maxTextWidth = Math.max(
    0,
    ...textLines.map(line => calculateText3DLineWidth(line, fontSize, widthScale)),
  )
  return maxTextWidth + 2 * marginWorld
}

/** Background plane height used by Text3DThree (without a fixed `boxSize`). */
export const calculateText3DLabelRectangleHeight = (
  numLines: number,
  fontSize: number,
  lineGapFactor = TEXT_3D_LINE_GAP_FACTOR,
  marginWorld = TEXT_3D_LABEL_MARGIN_WORLD,
): number => numLines * fontSize + numLines * (fontSize * lineGapFactor) * 2 + 2 * marginWorld
